import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Home, Building2, Car, AlertTriangle, Key, Lock, Settings, ShieldCheck, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const PHONE_NUMBER = "306-262-5995";

const ServicesPage = () => {
  const services = [
    {
      id: "residential",
      title: "Residential Locksmith",
      description: "Complete home security solutions. From lockouts to full security upgrades, we keep your family safe.",
      icon: <Home className="w-12 h-12" />,
      image: "https://images.pexels.com/photos/20901468/pexels-photo-20901468.jpeg",
      features: ["Home Lockouts", "Lock Replacement", "Rekeying Services", "Smart Lock Installation", "Deadbolt Installation", "Security Upgrades"],
      link: "/residential"
    },
    {
      id: "commercial",
      title: "Commercial Locksmith",
      description: "Protect your business with professional-grade security solutions and access control systems.",
      icon: <Building2 className="w-12 h-12" />,
      image: "https://images.pexels.com/photos/279810/pexels-photo-279810.jpeg",
      features: ["Master Key Systems", "Access Control", "High-Security Locks", "Panic Bar Installation", "Office Lockouts", "Safe Services"],
      link: "/commercial"
    },
    {
      id: "automotive",
      title: "Car Locksmith",
      description: "Locked out of your car? Lost your keys? We handle all vehicle makes and models.",
      icon: <Car className="w-12 h-12" />,
      image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d",
      features: ["Car Lockouts", "Key Programming", "Transponder Keys", "Ignition Repair", "Key Fob Replacement", "Broken Key Extraction"],
      link: "/automotive"
    },
    {
      id: "emergency",
      title: "24/7 Emergency",
      description: "Day or night, we're here when you need us most. Fast response guaranteed.",
      icon: <AlertTriangle className="w-12 h-12" />,
      image: "https://images.pexels.com/photos/279810/pexels-photo-279810.jpeg",
      features: ["24/7 Availability", "15-30 Min Response", "All Lock Types", "No Extra Night Fees", "Holiday Service", "Senior Discounts"],
      link: "/emergency"
    },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Our <span className="text-amber-400">Services</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
            Complete locksmith solutions for residential, commercial, and automotive needs. Available 24/7 across Saskatoon and surrounding areas.
          </p>
          <a href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`}>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg px-8 py-6">
              <Phone className="w-5 h-5 mr-2" />
              Call Now: {PHONE_NUMBER}
            </Button>
          </a>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div key={service.id} className="bg-slate-800 border border-slate-700 rounded-sm overflow-hidden group">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="text-amber-400 mb-4">{service.icon}</div>
                  <h2 className="font-heading text-2xl text-white mb-3">{service.title}</h2>
                  <p className="text-slate-400 mb-6">{service.description}</p>
                  <ul className="grid grid-cols-2 gap-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-slate-300 text-sm flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-slate-900">
                      Learn More <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 bg-amber-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 mb-4">
            Need Help Right Now?
          </h2>
          <p className="text-slate-700 text-lg mb-8">
            Our team is standing by 24/7. Call us for fast, reliable service.
          </p>
          <a href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`}>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xl px-10 py-6">
              <Phone className="w-6 h-6 mr-3" />
              {PHONE_NUMBER}
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
